import { useState, useEffect } from 'react';
import { Alert, Spinner, Tab, Nav } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config';

function ProfilePage() {
    const { user, setUser } = useAuth();

    // Dados do perfil
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState('');
    const [profileErr, setProfileErr] = useState({});

    // Senha
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);
    const [savingPw, setSavingPw] = useState(false);
    const [pwMsg, setPwMsg] = useState('');
    const [pwErr, setPwErr] = useState('');

    // Stats
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    useEffect(() => {
        if (user) { setUsername(user.username || ''); setEmail(user.email || ''); }
    }, [user]);

    useEffect(() => {
        axios.get(`${API_URL}/api/profile/stats`, { withCredentials: true })
            .then(r => setStats(r.data))
            .catch(() => {})
            .finally(() => setLoadingStats(false));
    }, []);

    const initials = (name) =>
        (name || '?').split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true); setProfileMsg(''); setProfileErr({});
        try {
            const res = await axios.put(`${API_URL}/api/profile`, { username, email }, { withCredentials: true });
            setProfileMsg(res.data.message);
            setUser(prev => ({ ...prev, username: res.data.username, email: res.data.email }));
        } catch (err) {
            if (err.response?.data?.errors) setProfileErr(err.response.data.errors);
            else setProfileErr({ general: err.response?.data?.error || 'Erro ao salvar.' });
        } finally {
            setSavingProfile(false);
        }
    };

    const pwStrength = (pw) => {
        if (!pw) return null;
        let score = 0;
        if (pw.length >= 8) score++;
        if (pw.length >= 12) score++;
        if (/[A-Z]/.test(pw)) score++;
        if (/[0-9]/.test(pw)) score++;
        if (/[^A-Za-z0-9]/.test(pw)) score++;
        if (score <= 1) return { label: 'Fraca', color: '#dc2626', pct: 25 };
        if (score <= 2) return { label: 'Razoável', color: '#f59e0b', pct: 50 };
        if (score <= 3) return { label: 'Boa', color: '#3b82f6', pct: 75 };
        return { label: 'Forte', color: '#16a34a', pct: 100 };
    };

    const handleChangePw = async (e) => {
        e.preventDefault();
        setPwErr(''); setPwMsg('');
        if (newPw !== confirmPw) { setPwErr('As senhas não coincidem.'); return; }
        if (newPw.length < 6) { setPwErr('A nova senha deve ter pelo menos 6 caracteres.'); return; }
        setSavingPw(true);
        try {
            const res = await axios.put(`${API_URL}/api/profile/password`,
                { current_password: currentPw, new_password: newPw }, { withCredentials: true });
            setPwMsg(res.data.message);
            setCurrentPw(''); setNewPw(''); setConfirmPw('');
        } catch (err) {
            setPwErr(err.response?.data?.error || 'Erro ao alterar senha.');
        } finally {
            setSavingPw(false);
        }
    };

    const strength = pwStrength(newPw);
    const fmt = (n) => n?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '—';

    return (
        <div className="bc-page">
            <div className="container" style={{ maxWidth: '680px' }}>

                {/* Header do perfil */}
                <div className="bc-section mb-3" style={{ background: 'linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%)', color: '#fff', borderLeft: 'none' }}>
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                        <div style={{
                            width: 72, height: 72, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.22)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.7rem', fontWeight: 700, letterSpacing: '-1px',
                            border: '3px solid rgba(255,255,255,0.4)', flexShrink: 0,
                        }}>
                            {initials(user?.username)}
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 700 }}>{user?.username}</h4>
                            <p style={{ margin: 0, opacity: 0.8, fontSize: '0.9rem' }}>{user?.email}</p>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="d-flex gap-3 mt-3 flex-wrap">
                        {loadingStats ? <Spinner animation="border" size="sm" style={{ opacity: 0.6 }} /> : stats && (
                            <>
                                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.5rem 1rem', minWidth: '110px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{stats.n_benfeitorias}</div>
                                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Benfeitorias</div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.5rem 1rem', minWidth: '110px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{stats.n_lotes}</div>
                                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Lotes</div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.5rem 1rem', minWidth: '160px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>R$ {fmt(stats.valor_total)}</div>
                                    <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Valor total avaliado</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Abas */}
                <Tab.Container defaultActiveKey="dados">
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item><Nav.Link eventKey="dados">Informações</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="senha">Segurança</Nav.Link></Nav.Item>
                    </Nav>

                    <Tab.Content>
                        {/* ── Aba Informações ─────────────────────────────── */}
                        <Tab.Pane eventKey="dados">
                            <div className="bc-section">
                                <div className="bc-section-title">
                                    <span className="bc-section-num" style={{ background: '#4361ee' }}>👤</span>
                                    Dados da conta
                                </div>

                                {profileMsg && <Alert variant="success" dismissible onClose={() => setProfileMsg('')}>{profileMsg}</Alert>}
                                {profileErr.general && <Alert variant="danger" dismissible onClose={() => setProfileErr({})}>{profileErr.general}</Alert>}

                                <form onSubmit={handleSaveProfile}>
                                    <div className="mb-3">
                                        <label className="bc-label">Nome de usuário</label>
                                        <input
                                            type="text"
                                            className={`form-control bc-input${profileErr.username ? ' is-invalid' : ''}`}
                                            value={username}
                                            onChange={e => { setUsername(e.target.value); setProfileErr(p => ({ ...p, username: '' })); }}
                                            maxLength={64}
                                            required
                                        />
                                        {profileErr.username && <div className="invalid-feedback">{profileErr.username}</div>}
                                    </div>
                                    <div className="mb-4">
                                        <label className="bc-label">E-mail</label>
                                        <input
                                            type="email"
                                            className={`form-control bc-input${profileErr.email ? ' is-invalid' : ''}`}
                                            value={email}
                                            onChange={e => { setEmail(e.target.value); setProfileErr(p => ({ ...p, email: '' })); }}
                                            maxLength={120}
                                            required
                                        />
                                        {profileErr.email && <div className="invalid-feedback">{profileErr.email}</div>}
                                    </div>
                                    <button type="submit" className="btn bc-btn bc-btn-primary" disabled={savingProfile}>
                                        {savingProfile ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : 'Salvar alterações'}
                                    </button>
                                </form>
                            </div>
                        </Tab.Pane>

                        {/* ── Aba Segurança ────────────────────────────────── */}
                        <Tab.Pane eventKey="senha">
                            <div className="bc-section">
                                <div className="bc-section-title">
                                    <span className="bc-section-num" style={{ background: '#4361ee' }}>🔒</span>
                                    Alterar senha
                                </div>

                                {pwMsg && <Alert variant="success" dismissible onClose={() => setPwMsg('')}>{pwMsg}</Alert>}
                                {pwErr && <Alert variant="danger" dismissible onClose={() => setPwErr('')}>{pwErr}</Alert>}

                                <form onSubmit={handleChangePw}>
                                    <div className="mb-3">
                                        <label className="bc-label">Senha atual</label>
                                        <div className="input-group">
                                            <input
                                                type={showCurrentPw ? 'text' : 'password'}
                                                className="form-control bc-input"
                                                value={currentPw}
                                                onChange={e => setCurrentPw(e.target.value)}
                                                required
                                                autoComplete="current-password"
                                            />
                                            <button type="button" className="btn btn-outline-secondary"
                                                onClick={() => setShowCurrentPw(p => !p)}
                                                tabIndex={-1}>
                                                {showCurrentPw ? '🙈' : '👁️'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="bc-label">Nova senha</label>
                                        <div className="input-group">
                                            <input
                                                type={showNewPw ? 'text' : 'password'}
                                                className="form-control bc-input"
                                                value={newPw}
                                                onChange={e => setNewPw(e.target.value)}
                                                required
                                                autoComplete="new-password"
                                            />
                                            <button type="button" className="btn btn-outline-secondary"
                                                onClick={() => setShowNewPw(p => !p)}
                                                tabIndex={-1}>
                                                {showNewPw ? '🙈' : '👁️'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Barra de força da senha */}
                                    {newPw && strength && (
                                        <div className="mb-3">
                                            <div style={{ height: 4, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
                                                <div style={{ width: `${strength.pct}%`, height: '100%', background: strength.color, borderRadius: 4, transition: 'width 0.3s' }} />
                                            </div>
                                            <small style={{ color: strength.color, fontWeight: 600 }}>Força: {strength.label}</small>
                                        </div>
                                    )}

                                    <div className="mb-4">
                                        <label className="bc-label">Confirmar nova senha</label>
                                        <input
                                            type="password"
                                            className={`form-control bc-input${confirmPw && confirmPw !== newPw ? ' is-invalid' : confirmPw && confirmPw === newPw ? ' is-valid' : ''}`}
                                            value={confirmPw}
                                            onChange={e => setConfirmPw(e.target.value)}
                                            required
                                            autoComplete="new-password"
                                        />
                                        {confirmPw && confirmPw !== newPw && (
                                            <div className="invalid-feedback">As senhas não coincidem.</div>
                                        )}
                                        {confirmPw && confirmPw === newPw && (
                                            <div className="valid-feedback">Senhas coincidem.</div>
                                        )}
                                    </div>

                                    <button type="submit" className="btn bc-btn bc-btn-primary"
                                        disabled={savingPw || !currentPw || !newPw || newPw !== confirmPw}>
                                        {savingPw ? <><Spinner animation="border" size="sm" className="me-2" />Alterando...</> : 'Alterar senha'}
                                    </button>
                                </form>
                            </div>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </div>
        </div>
    );
}

export default ProfilePage;
